/*
 * Why the map did not load, in words the admin can act on.
 *
 * When Google refuses a key it paints a grey box reading "Sorry! Something went
 * wrong. This page didn't load Google Maps correctly. See the JavaScript
 * console for technical details" - and the actual reason, which is one short
 * code like BillingNotEnabledMapError, goes to the browser console where nobody
 * running a school will ever look. The screen that needs fixing then looks
 * simply broken, and every way of adding a stop stops working with no
 * explanation, because all of them go through Maps.
 *
 * Two things are captured here. gm_authFailure is Google's own callback for a
 * rejected key. And Google writes the specific code to console.error before
 * calling it, so console.error is wrapped just long enough to read that code
 * out - it is still passed through to the real console untouched.
 */
import { reactive } from "@vue/composition-api";
import { loadGmapApi } from "vue2-google-maps";

export const mapsStatus = reactive({
  //idle | loading | ready | failed
  state: "idle",
  //the code Google logged, when we caught it: e.g. RefererNotAllowedMapError
  code: null,
  //why we think it failed, when there is no code: no-key | script | auth
  reason: null,
});

let installed = false;
let requested = false;

const MAP_ERROR = /\b([A-Za-z]+MapError)\b/;

function captureConsoleErrors() {
  const original = window.console && window.console.error;
  if (!original) return;

  window.console.error = function (...args) {
    try {
      const text = args
        .map((arg) => (typeof arg === "string" ? arg : ""))
        .join(" ");
      const match = MAP_ERROR.exec(text);
      if (match) {
        mapsStatus.state = "failed";
        mapsStatus.code = match[1];
      }
    } catch (e) {
      //never let diagnostics break logging
    }

    return original.apply(window.console, args);
  };
}

export function installMapsDiagnostics() {
  if (installed) return;
  installed = true;

  captureConsoleErrors();

  //Google calls this itself when it rejects the key: wrong key, referrer not
  //allowed, or no billing account on the project
  window.gm_authFailure = function () {
    mapsStatus.state = "failed";
    if (!mapsStatus.reason) mapsStatus.reason = "auth";
  };
}

/**
 * Load the Maps API once, and record what happened.
 *
 * @param {string} key the browser key, baked in at build time
 */
export function loadMaps(key) {
  installMapsDiagnostics();

  if (mapsStatus.state === "ready" || requested) {
    return;
  }
  requested = true;

  if (!key) {
    //the build had no VUE_APP_GOOGLE_MAPS_API_KEY, so the request would go out
    //asking for key=undefined and come back refused
    mapsStatus.state = "failed";
    mapsStatus.reason = "no-key";
    return;
  }

  mapsStatus.state = "loading";

  loadGmapApi({
    key: key,
    libraries: "places, geocoding",
  });

  //vue2-google-maps gives no failure callback, so the script tag it appends is
  //watched directly: a network-level failure never reaches gm_authFailure
  window.setTimeout(() => {
    const script = document.querySelector('script[src*="maps.googleapis.com"]');
    if (script) {
      script.addEventListener("error", () => {
        mapsStatus.state = "failed";
        mapsStatus.reason = "script";
      });
    }
    if (window.google && window.google.maps && mapsStatus.state === "loading") {
      mapsStatus.state = "ready";
    }
  }, 0);
}

/** Called by the map component once the API object actually appears. */
export function markMapsReady() {
  if (mapsStatus.state !== "failed") {
    mapsStatus.state = "ready";
  }
}

/**
 * The i18n key explaining what to do about it.
 *
 * Google's codes are precise and unreadable; each maps to one sentence saying
 * which setting to change and where.
 */
export function mapsFixKey() {
  if (mapsStatus.code) {
    const known = {
      BillingNotEnabledMapError: "maps_fix_billing",
      ApiNotActivatedMapError: "maps_fix_api",
      RefererNotAllowedMapError: "maps_fix_referrer",
      InvalidKeyMapError: "maps_fix_key",
      ExpiredKeyMapError: "maps_fix_key",
      MissingKeyMapError: "maps_fix_key",
      ApiTargetBlockedMapError: "maps_fix_api",
    };
    if (known[mapsStatus.code]) return known[mapsStatus.code];
  }

  if (mapsStatus.reason === "no-key") return "maps_fix_no_key";
  if (mapsStatus.reason === "script") return "maps_fix_network";

  //gm_authFailure with no code caught: it is one of the three key problems
  return "maps_fix_auth";
}
